/**
 * Converte o horário que o dono escreve no painel para o formato do
 * schema.org (`openingHours`).
 *
 * O campo é TEXTO LIVRE — "Seg a Sex 8h às 18h · Sáb 8h às 13h" — porque é o
 * que aparece no rodapé para o cliente ler. O Google, porém, só entende
 * `"Mo-Fr 08:00-18:00"`.
 *
 * REGRA DESTE MÓDULO: na dúvida, OMITE. Dado estruturado errado é pior que
 * dado estruturado ausente — um horário inventado leva cliente à porta
 * fechada, e o Google pode penalizar a inconsistência com o perfil da empresa.
 * Todo trecho que não casar com confiança é descartado em silêncio.
 */
/** Abreviações do schema.org, em inglês. */
const DIAS = {
    dom: 'Su',
    seg: 'Mo',
    ter: 'Tu',
    qua: 'We',
    qui: 'Th',
    sex: 'Fr',
    sab: 'Sa',
};
function removerAcentos(texto) {
    return texto.normalize('NFD').replace(/[̀-ͯ]/g, '');
}
/** "seg", "segunda", "segunda-feira" -> "Mo"; desconhecido -> null. */
function diaParaSchema(bruto) {
    const limpo = removerAcentos(bruto).toLowerCase().trim();
    for (const [prefixo, sigla] of Object.entries(DIAS)) {
        if (limpo.startsWith(prefixo))
            return sigla;
    }
    return null;
}
/** "8h" / "8h30" / "8:30" / "18" -> "08:00" / "08:30" / "08:30" / "18:00". */
function horaParaSchema(bruto) {
    const casou = /^(\d{1,2})\s*(?:h|:)?\s*(\d{2})?/.exec(bruto.trim());
    if (!casou)
        return null;
    const hora = Number(casou[1]);
    const minuto = Number(casou[2] ?? '0');
    if (!Number.isInteger(hora) || hora > 23 || minuto > 59)
        return null;
    return `${String(hora).padStart(2, '0')}:${String(minuto).padStart(2, '0')}`;
}
/**
 * "Seg a Sex 8h às 18h · Sáb 8h às 13h"
 *   -> ["Mo-Fr 08:00-18:00", "Sa 08:00-13:00"]
 *
 * Devolve `[]` quando nada pôde ser interpretado — e aí o chamador OMITE o
 * `openingHours` em vez de publicar um palpite.
 */
export function horarioParaSchema(texto) {
    if (!texto.trim())
        return [];
    const saida = [];
    // O dono separa os blocos com "·", ";" ou "|". A vírgula fica de fora de
    // propósito: ela aparece DENTRO de um bloco ("Seg a Sex, 8h às 18h").
    for (const bloco of texto.split(/[·;|]/)) {
        const limpo = removerAcentos(bloco).toLowerCase().replace(/\s+/g, ' ').trim();
        if (!limpo)
            continue;
        // dias: "seg a sex", "seg-sex" ou um dia só
        const faixaDias = /^([a-z]+)\s*(?:a|-|até|as)\s*([a-z]+)\b/.exec(limpo);
        const diaUnico = /^([a-z]+)\b/.exec(limpo);
        let dias = null;
        if (faixaDias) {
            const de = diaParaSchema(faixaDias[1]);
            const ate = diaParaSchema(faixaDias[2]);
            if (de && ate)
                dias = `${de}-${ate}`;
        }
        if (!dias && diaUnico)
            dias = diaParaSchema(diaUnico[1]);
        if (!dias)
            continue;
        // horas: o primeiro par de números do bloco
        const horas = limpo.match(/\d{1,2}(?:\s*[h:]\s*\d{2})?/g);
        if (!horas || horas.length < 2)
            continue;
        const abre = horaParaSchema(horas[horas.length - 2]);
        const fecha = horaParaSchema(horas[horas.length - 1]);
        if (!abre || !fecha || abre === fecha)
            continue;
        saida.push(`${dias} ${abre}-${fecha}`);
    }
    return saida;
}
//# sourceMappingURL=horario.js.map